import Cookies, { CookieAttributes } from 'js-cookie';

import environment from '../../environments';

export enum FetchMethod {
  Get = 'GET',
  Patch = 'PATCH',
  Post = 'POST',
  Put = 'PUT',
  Delete = 'DELETE'
}

export interface FetchError {
  statusCode: number;
  error: string;
  message: string;
}

export interface FetchRequest {
  response: Promise<Response>;
  abort: () => void;
}

export interface ApiParams {
  [key: string]: any;
}

const cookieAttributes: CookieAttributes = {
  domain: environment.domain || window.location.hostname,
  secure: environment.secure || false,
  // default to 'none' for non-development environment
  sameSite: environment.sameSite || 'none'
};

const api = {
  getBaseUrl: (): string => environment.apiBaseUrl,
  setCookie: (key: string, value: string, options?: CookieAttributes) => {
    Cookies.set(key, value, { ...cookieAttributes, ...options });
  },
  getCookie: (key: string) => {
    return Cookies.get(key);
  },
  clearCookie: (key: string) => {
    Cookies.remove(key, cookieAttributes);
  },
  getAccessToken: (): string | undefined => {
    return api.getCookie(environment.accessTokenKey);
  },
  setAccessToken: (token: string, key?: string, expiresIn?: number) => {
    api.setCookie(key ? key : environment.accessTokenKey, token, {
      expires: expiresIn ? expiresIn : environment.expires || 365
    });
  },
  removeAccessToken: (key?: string) => {
    api.clearCookie(key ? key : environment.accessTokenKey);
  },
  get: (
    path: string,
    queryParams: ApiParams,
    extraHeaders?: Record<string, string>
  ): FetchRequest => {
    return api.fetch(FetchMethod.Get, path, queryParams, extraHeaders);
  },
  delete: (
    path: string,
    queryParams: ApiParams,
    extraHeaders?: Record<string, string>
  ): FetchRequest => {
    return api.fetch(FetchMethod.Delete, path, queryParams, extraHeaders);
  },
  patch: (
    path: string,
    body: ApiParams,
    extraHeaders?: Record<string, string>
  ): FetchRequest => {
    return api.fetch(FetchMethod.Patch, path, body, extraHeaders);
  },
  post: (
    path: string,
    body: ApiParams,
    extraHeaders?: Record<string, string>
  ): FetchRequest => {
    return api.fetch(FetchMethod.Post, path, body, extraHeaders);
  },
  put: (
    path: string,
    body: ApiParams,
    extraHeaders?: Record<string, string>
  ): FetchRequest => {
    return api.fetch(FetchMethod.Put, path, body, extraHeaders);
  },
  fetch: (
    method: FetchMethod,
    path: string,
    params: ApiParams = {},
    extraHeaders: Record<string, string> = {}
  ): FetchRequest => {
    const controller = new AbortController();
    const { signal } = controller; // allow aborting the fetch call
    const url = new URL(path, environment.apiBaseUrl);
    // console.log('url', url);
    const headers = {
      [environment.accessTokenKey]: `Bearer ${api.getAccessToken()}`,
      ...extraHeaders
    } as any;
    const options: RequestInit = {
      headers,
      method,
      signal
    };
    switch (method) {
      case FetchMethod.Get:
      case FetchMethod.Delete:
        Object.entries(params).forEach(([key, value]) => {
          url.searchParams.set(
            key,
            typeof value === 'object' ? JSON.stringify(value) : value
          );
        });
        break;
      case FetchMethod.Patch:
      case FetchMethod.Post:
      case FetchMethod.Put:
        (options.headers as any)['content-type'] = 'application/json';
        options.body = JSON.stringify(params);
        break;
    }
    return {
      response: fetch(url.toString(), options),
      abort: controller.abort.bind(controller)
    };
  }
};

export default api;
